import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import type { OneOffSession, OneOffAskRequest, OneOffAskResponse } from "@/api/client";
import { apiClient } from "@/api/client";
import { useCredits } from "@/hooks/useCredits";
import SessionHeader from "./SessionHeader";
import ContextBar from "./ContextBar";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface OneOffChatProps {
  session: OneOffSession;
  onNewSession: () => void;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  responseId?: string;
  verdict?: "answer" | "insufficient";
  model?: string;
  contextReset?: boolean;
  costUsd?: number;
}

const formatMessageForCopy = (message: ChatMessage) => {
  const sections: string[] = [];
  const body = (message.content ?? "").trimEnd();

  if (body) {
    sections.push(body);
  }

  const meta: string[] = [];
  if (message.role === "assistant") {
    if (message.verdict) {
      const verdictLabel = message.verdict === "answer" ? "Answer provided" : "Insufficient context";
      meta.push(`Verdict: ${verdictLabel}`);
    }
    if (message.model) {
      meta.push(`Model: ${message.model}`);
    }
    if (message.costUsd != null) {
      meta.push(`Cost: $${message.costUsd.toFixed(4)}`);
    }
  }

  if (meta.length) {
    sections.push(meta.join("\n"));
  }

  return sections.join("\n\n---\n\n") || body;
};

export default function OneOffChat({ session, onNewSession }: OneOffChatProps) {
  const { refreshCredits } = useCredits();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastResponseId, setLastResponseId] = useState<string | undefined>(undefined);
  const [copiedChat, setCopiedChat] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [lastInputTokens, setLastInputTokens] = useState<number | undefined>(undefined);

  // Episode inclusion state - start with all ready episodes included
  const [includedIndices, setIncludedIndices] = useState<Set<number>>(() => {
    const ready = session.episodes.filter((e) => e.status === "ready");
    return new Set(ready.map((e) => e.index));
  });

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const toggleInclude = (index: number) => {
    setIncludedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const readyEpisodes = session.episodes.filter((e) => e.status === "ready");
  const totalIncludedTokens = readyEpisodes
    .filter((e) => includedIndices.has(e.index))
    .reduce((sum, e) => sum + (e.token_count ?? 0), 0);
  const modelContextLimit = session.model_context_limit ?? 400000;
  const isOverLimit = totalIncludedTokens > modelContextLimit;

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isOverLimit) return;
    if (includedIndices.size === 0) {
      setError("Please include at least one episode in your context.");
      return;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputMessage.trim(),
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    setError("");

    try {
      const payload: OneOffAskRequest = {
        question: userMessage.content,
        include_episodes: Array.from(includedIndices),
      };

      if (lastResponseId) {
        payload.previous_response_id = lastResponseId;
      }

      const response: OneOffAskResponse = await apiClient.askOneOffSession(payload);

      refreshCredits();

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.answer,
        timestamp: Date.now(),
        responseId: response.response_id,
        verdict: response.verdict,
        model: response.model,
        contextReset: response.usage.context_reset,
        costUsd: response.usage.cost_usd,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setLastResponseId(response.response_id);
      setLastInputTokens(response.usage.input_tokens);

      if (response.usage.context_reset) {
        setError("Conversation context was reset due to length. You can continue asking questions.");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Something went wrong";
      setError(errorMessage);

      const errorAssistantMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: `I encountered an error: ${errorMessage}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorAssistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setLastResponseId(undefined);
    setError("");
  };

  const markdownComponents = useMemo(() => {
    const baseClass = "text-gray-900 dark:text-gray-100";
    return {
      p: ({ children, ...props }: any) => (
        <p className={`mb-4 leading-relaxed ${baseClass}`} {...props}>
          {children}
        </p>
      ),
      h1: ({ children, ...props }: any) => (
        <h1 className={`mt-6 mb-4 text-2xl font-semibold ${baseClass}`} {...props}>
          {children}
        </h1>
      ),
      h2: ({ children, ...props }: any) => (
        <h2 className={`mt-5 mb-3 text-xl font-semibold ${baseClass}`} {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }: any) => (
        <h3 className={`mt-4 mb-2 text-lg font-semibold ${baseClass}`} {...props}>
          {children}
        </h3>
      ),
      a: ({ children, ...props }: any) => (
        <a
          className="font-semibold text-gray-700 underline decoration-2 underline-offset-4 hover:text-gray-900 dark:text-amber-400 dark:hover:text-amber-200"
          target="_blank"
          rel="noreferrer"
          {...props}
        >
          {children}
        </a>
      ),
      ul: ({ children, ...props }: any) => (
        <ul className={`mb-4 list-disc space-y-2 pl-6 ${baseClass}`} {...props}>
          {children}
        </ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol className={`mb-4 list-decimal space-y-2 pl-6 ${baseClass}`} {...props}>
          {children}
        </ol>
      ),
      li: ({ children, ...props }: any) => (
        <li className="leading-relaxed" {...props}>
          {children}
        </li>
      ),
      blockquote: ({ children, ...props }: any) => (
        <blockquote
          className="my-4 border-l-4 border-gray-300 bg-gray-100/60 pl-4 italic text-gray-700 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200"
          {...props}
        >
          {children}
        </blockquote>
      ),
      code: ({ inline, className, children, ...props }: any) => {
        const codeContent = Array.isArray(children) ? children.join("") : String(children ?? "");
        if (inline) {
          return (
            <code
              className="rounded-md bg-gray-200 px-1.5 py-0.5 font-mono text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-100"
              {...props}
            >
              {codeContent}
            </code>
          );
        }
        return (
          <pre className="my-5 overflow-x-auto rounded-xl border border-gray-200 bg-gray-900 p-4 text-sm text-gray-100 dark:border-gray-700">
            <code className={`font-mono ${className ?? ""}`} {...props}>
              {codeContent.replace(/\n$/, "")}
            </code>
          </pre>
        );
      },
      table: ({ children, ...props }: any) => (
        <div className="my-4 overflow-x-auto">
          <table className={`w-full border-collapse text-left text-sm ${baseClass}`} {...props}>
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }: any) => (
        <thead className="bg-gray-100 dark:bg-gray-800" {...props}>
          {children}
        </thead>
      ),
      th: ({ children, ...props }: any) => (
        <th className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100" {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }: any) => (
        <td className="border-b border-gray-200 px-3 py-2 align-top text-gray-700 dark:border-gray-700 dark:text-gray-200" {...props}>
          {children}
        </td>
      ),
      hr: (props: any) => <hr className="my-6 border-dashed border-gray-300 dark:border-gray-700" {...props} />,
    };
  }, []);

  return (
    <div className="space-y-4">
      <SessionHeader
        feedTitle={session.feed_title}
        expiresAt={session.expires_at}
        onNewSession={onNewSession}
      />

      <ContextBar
        episodes={session.episodes}
        includedIndices={includedIndices}
        onToggleInclude={toggleInclude}
        modelContextLimit={modelContextLimit}
        actualInputTokens={lastInputTokens}
      />

      {/* Chat header with actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Chat
        </h2>
        {messages.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={() => {
                const formatted = messages
                  .map((msg) => {
                    const sender = msg.role === "user" ? "You" : "AI Assistant";
                    const time = new Date(msg.timestamp).toLocaleTimeString();
                    const body = formatMessageForCopy(msg);
                    return `## ${sender} (${time})\n\n${body}\n\n`;
                  })
                  .join("---\n\n");
                void navigator.clipboard.writeText(formatted);
                setCopiedChat(true);
                setTimeout(() => setCopiedChat(false), 2000);
              }}
              className={cn(
                theme.buttons.secondary,
                "px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              )}
            >
              {copiedChat ? (
                <>
                  <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </>
              )}
            </button>
            <button
              onClick={clearChat}
              className={cn(
                theme.buttons.secondary,
                "px-3 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400"
              )}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className={cn(
          "p-4 rounded-xl",
          theme.borders.error,
          theme.colors.errorBg,
          theme.text.error
        )}>
          {error}
        </div>
      )}

      {/* Chat container */}
      <div className="relative">
        <div className={cn("absolute inset-0 rounded-xl translate-y-2 translate-x-2", theme.colors.slate950)} />
        <div
          ref={chatContainerRef}
          className={cn(
            "relative z-10 p-4 min-h-[400px] max-h-[500px] overflow-y-auto",
            theme.cards.episode
          )}
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400 py-16">
              <p className="text-center mb-2">Start chatting about these episodes!</p>
              <p className="text-center text-sm">
                Ask questions about the content, themes, or key points.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={message.role === "user" ? "ml-auto max-w-[85%]" : "mr-auto max-w-[85%]"}
                >
                  <div
                    className={cn(
                      "relative p-4 rounded-xl",
                      message.role === "user"
                        ? "bg-blue-100 dark:bg-blue-900 border-3 border-blue-500 dark:border-blue-700"
                        : "bg-gray-100 dark:bg-gray-700 border-3 border-gray-900 dark:border-gray-600"
                    )}
                  >
                    <div className="flex justify-between items-start mb-2 gap-3">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {message.role === "user" ? "You" : "AI Assistant"}
                      </div>
                      <button
                        onClick={() => {
                          void navigator.clipboard.writeText(formatMessageForCopy(message));
                          setCopiedMessageId(message.id);
                          setTimeout(() => setCopiedMessageId(null), 2000);
                        }}
                        className="text-xs text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white transition-colors"
                      >
                        {copiedMessageId === message.id ? "Copied" : "Copy"}
                      </button>
                    </div>

                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>
                      {message.content}
                    </ReactMarkdown>

                    <div className="mt-3 text-xs text-gray-500 dark:text-gray-300">
                      <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                      {message.verdict && (
                        <span className="ml-2 font-semibold">
                          {message.verdict === "answer" ? "Answer provided" : "Insufficient context"}
                        </span>
                      )}
                      {message.model && <span className="ml-2">Model: {message.model}</span>}
                      {message.costUsd != null && (
                        <span className="ml-2">Cost: ${message.costUsd.toFixed(4)}</span>
                      )}
                      {message.contextReset && (
                        <span className="ml-2 text-amber-600 dark:text-amber-400">Context reset</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="mr-auto max-w-[85%]">
                  <div className={cn("relative p-4 rounded-xl bg-gray-100 dark:bg-gray-700", theme.borders.neubrutalism)}>
                    <div className="flex space-x-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "0ms" }} />
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "150ms" }} />
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="relative">
        <div className={cn("absolute inset-0 rounded-xl translate-y-2 translate-x-2", theme.colors.slate950)} />
        <div className={cn("relative z-10 bg-white dark:bg-gray-800 rounded-xl p-4", theme.borders.slate950)}>
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendMessage();
              }
            }}
            placeholder="Ask about these episodes..."
            className={cn(
              "w-full min-h-[100px] bg-transparent border-2 border-gray-200 dark:border-gray-600 rounded-xl p-3",
              "text-gray-900 dark:text-gray-100",
              "focus:outline-none focus:ring-2",
              theme.ring.primaryOrange
            )}
            disabled={isOverLimit}
          />
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-500 dark:text-gray-300">
              Shift+Enter for newline
            </div>
            <button
              onClick={() => void sendMessage()}
              disabled={isLoading || !inputMessage.trim() || isOverLimit || includedIndices.size === 0}
              className={cn(
                theme.buttons.primary,
                "px-5 py-2 rounded-xl",
                "disabled:cursor-not-allowed disabled:opacity-75 disabled:hover:translate-y-0"
              )}
            >
              {isLoading ? "Thinking..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
